import { Box, Flex, Grid, Image, Spinner } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Stages } from 'src/providers/Cartridge/cartridgeProvider';
import { Color } from 'src/theme/color';

const ProgressItem = ({
	children,
	small = false,
	isLoading = false,
	completed = false,
}: {
	children: React.ReactNode;
	completed?: boolean;
	small?: boolean;
	isLoading?: boolean;
}) => {
	return (
		<Grid
			h="30px"
			alignItems="center"
			gap="15px"
			templateColumns="30px auto"
		>
			<Flex
				justifyContent="center"
				align="center"
				boxSize="30px"
			>
				{!small &&
					(!completed ? (
						isLoading ? (
							<Spinner
								width={18}
								height={18}
								color="red.500"
								thickness="4px"
							/>
						) : (
							<Image src="/assets/icons/clock.svg" />
						)
					) : (
						<Image src="/assets/icons/completed.svg" />
					))}
				{small && (
					<Box
						boxSize="2px"
						bg="gray"
					/>
				)}
			</Flex>
			<Flex
				gap="5px"
				lineHeight="30px"
				justify="flex-start"
				fontSize={small ? '14px' : '24px'}
				fontWeight={small ? 'regular' : 'bold'}
			>
				{small && (
					<Flex
						align="center"
						w="16px"
					>
						{isLoading ? (
							<Spinner
								width="14px"
								height="14px"
							/>
						) : (
							completed && <Image src="/assets/icons/check.svg" />
						)}
					</Flex>
				)}
				{children}
			</Flex>
		</Grid>
	);
};

export const FinalizationProgress = ({ stage }: { stage: Stages }) => {
	const { t } = useTranslation();

	return (
		<Box padding="70px 70px">
			<Box
				ml="46px"
				mb="20px"
				fontSize="14px"
				fontWeight="bold"
				color={Color.HASHUP}
			>
				Status
			</Box>
			<Flex
				flexDir="column"
				gap="10px"
				minH="280px"
			>
				<ProgressItem
					completed={stage >= Stages.DEPLOYED}
					isLoading={stage > Stages.UNDEPLOYED && stage < Stages.DEPLOYED}
				>
					{t('form.steps.finalization.step1')}
				</ProgressItem>
				{stage > Stages.UNDEPLOYED && stage < Stages.ALLOWANCE_CONFIRMATION && (
					<ProgressItem
						isLoading={stage == Stages.METADATA_UPLOAD}
						completed={stage > Stages.METADATA_UPLOAD}
						small
					>
						Uploading metadata to IPFS
					</ProgressItem>
				)}
				{stage > Stages.UNDEPLOYED && stage < Stages.ALLOWANCE_CONFIRMATION && (
					<ProgressItem
						isLoading={stage == Stages.CONFIRMATION}
						completed={stage > Stages.CONFIRMATION}
						small
					>
						{t('form.steps.finalization.step2')}
					</ProgressItem>
				)}
				{stage > Stages.UNDEPLOYED && stage < Stages.ALLOWANCE_CONFIRMATION && (
					<ProgressItem
						isLoading={stage == Stages.DEPLOYING}
						completed={stage >= Stages.DEPLOYED}
						small
					>
						Processing deployment
					</ProgressItem>
				)}
				<ProgressItem
					completed={stage >= Stages.IGO_ALLOWED}
					isLoading={stage > Stages.DEPLOYED && stage < Stages.IGO_ALLOWED}
				>
					{t('form.steps.finalization.step2')}
				</ProgressItem>
				{stage > Stages.DEPLOYED && stage < Stages.IGO_CONFIRMATION && (
					<ProgressItem
						isLoading={stage == Stages.ALLOWANCE_CONFIRMATION}
						completed={stage >= Stages.ALLOWING}
						small
					>
						Confirm contract interaction
					</ProgressItem>
				)}
				{stage > Stages.DEPLOYED && stage < Stages.IGO_CONFIRMATION && (
					<ProgressItem
						isLoading={stage == Stages.ALLOWING}
						completed={stage >= Stages.IGO_ALLOWED}
						small
					>
						Processing transaction
					</ProgressItem>
				)}

				<ProgressItem
					completed={stage >= Stages.IGO_SET}
					isLoading={stage > Stages.IGO_ALLOWED && stage < Stages.IGO_SET}
				>
					{t('form.steps.finalization.step3')}
				</ProgressItem>
				{stage > Stages.IGO_ALLOWED && stage < Stages.IGO_SET && (
					<ProgressItem
						isLoading={stage == Stages.IGO_CONFIRMATION}
						completed={stage >= Stages.IG_SETTING}
						small
					>
						Confirm contract interaction
					</ProgressItem>
				)}
				{stage > Stages.IGO_ALLOWED && stage < Stages.IGO_SET && (
					<ProgressItem
						isLoading={stage == Stages.IG_SETTING}
						completed={stage > Stages.IG_SETTING}
						small
					>
						Processing transaction
					</ProgressItem>
				)}
				<ProgressItem completed={stage >= Stages.IGO_SET}>{t('form.steps.finalization.step4')}</ProgressItem>
			</Flex>
		</Box>
	);
};
